import { Link, useNavigate } from '@tanstack/react-router';
import { RouterKey } from 'consts';
import { useLanguage } from 'context/language.context';
import { StringKey } from 'dictionaries/string-key.enum';
import { motion } from 'motion/react';
import React, { ReactNode } from 'react';

import { navFixtures } from './Header';
import { FacebookIcon } from './icons/FacebookIcon';
import { InstagramIcon } from './icons/InstagramIcon';
import { LinkedInIcon } from './icons/LinkedInIcon';
import { MaazeebLogo } from './icons/MaazeebLogo';
import { XIcon } from './icons/XIcon';
import { Button } from './ui/Button';

export interface SocialButtonFixture {
  icon: ReactNode;
  link: string;
}

export const socialButtonsFixtures: SocialButtonFixture[] = [
  { icon: <LinkedInIcon />, link: 'https://www.linkedin.com/company/maazeeb' },
  { icon: <XIcon />, link: 'https://x.com/maazeebksa?s=21&t=WY3aZoLfrXwkJOlGB_u8qw' },
  { icon: <FacebookIcon />, link: 'https://www.facebook.com/profile.php?id=61576321312093' },
  { icon: <InstagramIcon />, link: 'https://www.instagram.com/maazeebksa?igsh=eTVjeGhsN3A3cXVs' },
];

export const Footer = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const year = new Date().getFullYear();
  return (
    <motion.footer
      initial={{ opacity: 0, y: 150 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ ease: 'easeOut', duration: 0.6, delay: 0 }}
      className="relative w-full mt-auto z-10 border-t border-[#171221] bg-[#0B0910] px-20 pt-16 pb-12 flex flex-col gap-12 max-md:px-4 max-md:py-12"
    >
      <div className="flex flex-wrap justify-between gap-4 flex-col md:flex-row">
        <div className="flex items-start justify-between gap-6 max-md:flex-col max-md:gap-6">
          <div>
            <Link to={RouterKey.HOME_PAGE + '#product'}>
              <MaazeebLogo width={76} height={40} />
            </Link>
          </div>
          <div className="flex items-center text-sm max-md:grid max-md:w-full max-md:grid-cols-2">
            {[
              ...navFixtures,
              { title: StringKey.TERMS_OF_USE, href: RouterKey.TERMS_OF_USE },
              { title: StringKey.PRIVACY_POLICY, href: RouterKey.PRIVACY_POLICY },
            ].map((item, index) => (
              <Button
                key={index}
                variant="secondary"
                onClick={() => navigate({ to: item.href })}
                className="px-5 py-3 bg-transparent border-transparent max-md:px-0 max-md:justify-start max-md:border-0 max-md:hover:border-0 max-md:hover:bg-none"
              >
                {t(item.title as StringKey)}
              </Button>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap items-start justify-start gap-4 select-none">
          {/* TODO: Enable app download links when available
            <>
              <a href="/">
                <AppStoreBadge />
              </a>
              <a href="/">
                <GooglePlayBadge />
              </a>
            </>
          */}
        </div>
      </div>

      <div className="h-[24px] flex items-center justify-center">
        <img
          className="w-fit h-full block object-cover"
          src="/assets/footer-divider.png"
          alt="Footer divider"
        />
      </div>

      <div className="flex items-center justify-between max-md:flex-col-reverse max-md:items-start max-md:gap-4">
        <div className="font-normal">
          {t(StringKey.FOOTER_COPYRIGHT).replace('{year}', year.toString())}
        </div>
        <div className="flex items-center gap-3">
          {socialButtonsFixtures.map((item) => (
            <a
              key={item.link}
              href={item.link}
              target="_blank"
              rel="noreferrer"
              className="border border-transparent bg-origin-border bg-clip-border bg-[linear-gradient(180deg,rgba(255,255,255,0.2)_0%,rgba(255,255,255,0.06)_100%)] rounded-lg"
            >
              <div className="p-2 bg-[linear-gradient(144.42deg,#171221_0%,#0B0910_100%)] rounded-lg">
                {item.icon}
              </div>
            </a>
          ))}
        </div>
      </div>
    </motion.footer>
  );
};
